/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

/* eslint-disable max-classes-per-file */
import React from 'react';
import _ from 'lodash';
import { computed, decorate, observable, action, runInAction } from 'mobx';
import { observer, inject } from 'mobx-react';
import { Icon } from 'semantic-ui-react';
import { swallowError } from '@amzn/base-ui/dist/helpers/utils';
import {
  isStoreLoading,
  isStoreError,
  isStoreReady,
  stopHeartbeat,
} from '@amzn/base-ui/dist/models/BaseStore';
import BasicProgressPlaceholder from '@amzn/base-ui/dist/parts/helpers/BasicProgressPlaceholder';
import ErrorBox from '@amzn/base-ui/dist/parts/helpers/ErrorBox';

import StudyRow from './StudyRow';
import { categories } from '../../models/studies/categories';
import StepGuideCard from '../helpers/StepGuideCard';

// expected props
// - tab (an object with shape { id, name, storeIds: string[] })
// - studiesStoresMap (via injection)
// - userStore (via injection)
class StudiesTab extends React.Component {
  constructor(props) {
    super(props);
    runInAction(() => {
      this.searchTerm = '';
    });
  }

  get stores() {
    return _.compact(this.props.tab.storeIds.map(id => this.props.studiesStoresMap[id]));
  }

  componentDidMount() {
    this.stores.forEach(store => {
      if (!isStoreReady(store)) swallowError(store.load());
      store.startHeartbeat();
    });
  }

  componentWillUnmount() {
    this.stores.forEach(store => stopHeartbeat(store));
  }

  get isAdmin() {
    return _.get(this.props.userStore, 'user.isAdmin', false);
  }

  get showAsResearcher() {
    return this.isAdmin && this.props.userStore.researcherView;
  }

  get canCreateStudy() {
    if (this.showAsResearcher) return false;
    return _.get(this.props.userStore, 'user.capabilities.canCreateStudy', true) && this.hasProjects;
  }

  get getUserRole() {
    return _.get(this.props.userStore, 'user.userRole', true);
  }

  get canSelectStudy() {
    const can = _.get(this.props.userStore, 'user.capabilities.canSelectStudy', true);
    if (!can) return can;
    if (!this.isExternalUser) return this.hasProjects;
    return can;
  }

  get isExternalUser() {
    return _.get(this.props.userStore, 'user.isExternalUser', true);
  }

  get hasProjects() {
    return _.get(this.props.userStore, 'user.hasProjects', true);
  }

  get mergedList() {
    const allStudies = [];
    const filter = this.props.tab.filter;
    this.stores.forEach(store => {
      if (isStoreReady(store)) {
        store.list.forEach(study => {
          if (!filter || filter(study)) {
            allStudies.push(study);
          }
        });
      }
    });
    // Personal first, then Shared, then by createdAt desc within each group
    return _.orderBy(
      allStudies,
      [study => (study.isOrganizationStudy ? 1 : 0), 'createdAt', 'name'],
      ['asc', 'desc', 'asc'],
    );
  }

  get isAnyLoading() {
    return this.stores.some(store => isStoreLoading(store));
  }

  get isAnyError() {
    return this.stores.find(store => isStoreError(store));
  }

  get allReady() {
    return this.stores.every(store => isStoreReady(store));
  }

  render() {
    const errorStore = this.isAnyError;
    if (errorStore) {
      return <ErrorBox error={errorStore.error} className="mt3 mr0 ml0" />;
    }
    if (this.isAnyLoading && !this.allReady) {
      return <BasicProgressPlaceholder segmentCount={3} className="mt3 mr0 ml0" />;
    }
    if (this.allReady && this.mergedList.length === 0) {
      return this.renderEmpty();
    }
    return this.renderContent();
  }

  get isOpenData() {
    return this.props.tab.id === categories.openData.id;
  }

  handleSearchChange = (e) => {
    this.searchTerm = e.target.value;
  };

  renderMountingGuide() {
    const tabId = this.props.tab.id;
    const guideId = `studies-mount-hint-${tabId}`;

    let message;
    if (tabId === 'licenses') {
      message = 'To mount licenses to a workspace, visit the Workspaces page and click Create Workspace. You will be able to select licenses during the setup process.';
    } else if (tabId === categories.openData.id) {
      message = 'To mount open data studies to a workspace, visit the Workspaces page and click Create Workspace. You will be able to select studies during the setup process.';
    } else {
      message = 'To mount studies to a workspace, visit the Workspaces page and click Create Workspace. You will be able to select studies during the setup process.';
    }

    return (
      <StepGuideCard guideId={guideId} title="Looking to mount data to a workspace?">
        {message}
      </StepGuideCard>
    );
  }

  renderContent() {
    const getUserRole = this.getUserRole;
    const isOpenData = this.isOpenData;
    const searchTerm = this.searchTerm.toLowerCase().trim();
    let list = this.mergedList;

    if (isOpenData && searchTerm) {
      list = list.filter(study => (study.name || '').toLowerCase().includes(searchTerm));
    }

    return (
      <div className="mt2">
        {this.renderMountingGuide()}
        {isOpenData && (
          <div style={{ fontSize: '0.88em', color: '#6b7280', marginBottom: '12px' }}>
            To learn more about AWS Open Data, <a href="https://aws.amazon.com/opendata/" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--rmit-blue)', fontWeight: 500 }}>visit the AWS Open Data Registry</a>.
          </div>
        )}
        {isOpenData && (
          <div style={{
            position: 'relative',
            marginBottom: '16px',
          }}>
            <Icon name="search" style={{
              position: 'absolute',
              left: '14px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#9ca3af',
              margin: 0,
              lineHeight: 1,
              height: '1em',
              fontSize: '0.9em',
              pointerEvents: 'none',
            }} />
            <input
              type="text"
              placeholder="Search open data by name..."
              value={this.searchTerm}
              onChange={this.handleSearchChange}
              style={{
                width: '100%',
                padding: '10px 14px 10px 38px',
                border: '1px solid #e5e7eb',
                borderRadius: '10px',
                fontSize: '0.9em',
                fontFamily: 'inherit',
                outline: 'none',
                transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
              }}
              onFocus={e => { e.target.style.borderColor = 'var(--rmit-blue-50)'; e.target.style.boxShadow = '0 0 0 2px rgba(0, 0, 84, 0.08)'; }}
              onBlur={e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; }}
            />
          </div>
        )}
        {list.length === 0 ? (
          <div className="study-empty-state">
            <Icon name="search" />
            <div className="study-empty-state-title">No matching studies</div>
            <div className="study-empty-state-text">No studies match "{this.searchTerm}". Try a different search term.</div>
          </div>
        ) : (
          list.map(study => (
            <StudyRow key={study.id} study={study} userRole={getUserRole} isSelectable={false} />
          ))
        )}
      </div>
    );
  }

  renderEmpty() {
    const tabId = this.props.tab.id;
    const isOpenData = tabId === categories.openData.id;
    const isLicenses = tabId === 'licenses';
    const canCreateStudy = this.canCreateStudy;

    let title = 'No studies';
    let text = canCreateStudy ? 'To create a study, click on the Create Study button at the top.' : '';

    if (isLicenses) {
      title = 'You have not been assigned any licenses';
      text = 'If you require access to a specific license, please contact RACE.';
    } else if (isOpenData) {
      title = 'No studies from the Open Data project';
      text = 'The information in this page is updated once a day, please come back later.';
    } else {
      // Combined tab (my-studies + organization)
      title = 'No studies found';
      text = canCreateStudy
        ? 'You have no personal or shared studies yet. Create one using the Create Study button above.'
        : 'Consider viewing the Open Data studies by clicking on the Open Data tab above.';
    }

    return (
      <div className="study-empty-state mt3">
        <Icon name="clipboard outline" />
        <div className="study-empty-state-title">{title}</div>
        <div className="study-empty-state-text">{text}</div>
      </div>
    );
  }
}

decorate(StudiesTab, {
  stores: computed,
  isAdmin: computed,
  showAsResearcher: computed,
  canCreateStudy: computed,
  canSelectStudy: computed,
  hasProjects: computed,
  isExternalUser: computed,
  isOpenData: computed,
  mergedList: computed,
  isAnyLoading: computed,
  isAnyError: computed,
  allReady: computed,
  searchTerm: observable,
  handleSearchChange: action,
});

export default inject('studiesStoresMap', 'userStore')(observer(StudiesTab));
